import {
  Box,
  Grid,
  Skeleton,
  styled,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CardMedia from '@mui/material/CardMedia';
import Typography from '@mui/material/Typography';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ArrowLeftIcon from '@mui/icons-material/ArrowLeft';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import {
  carHotSpotCardOpenToggled,
  carHotSpotEnableSet,
  closeHotSpotPositionSet,
  currentCarHotSpotIndexSet,
  getCarHotSpotCardOpen,
  getCarHotSpotPositions,
  getCarStatus,
  getCurrentCarHotSpotIndex,
} from '../../store/car-slice';
import CustomizeBackdrop from '../../components/customize-backdrop';

import { getPitch, getYaw, pitchSet, yawSet } from '../../store/interior-slice';

import { getModelId, getUserId } from '../../store/car-slice';

import { Fab } from '../../components';
import constants from '../../config/constants';
import { handleDownloadFromGcs } from '../../services/gcs';
import ImageLoading from '../../components/image-loading/ImageLoading';
import theme from '../../config/theme';
import FabControlled from '../../components/fab/fab-controlled';
import useHotspotPositions from './use-hotspot-positions';
import { useWindowSize } from '../../hooks/use-window-size';
import { Report } from '../../types/common-types';
import SafetyIcon from '../../assets/icon/safety';
import ComfortIcon from '../../assets/icon/comfort';
import TechComputerIcon from '../../assets/icon/techComputer';
import LeftCurveIcon from '../../assets/icon/left-curve';
import RightCurveIcon from '../../assets/icon/right-curve';

const HotspotBox = styled(Box)<any>(({ orientation, content }) => ({
  backdropFilter: constants.BLUR_STYLE.filter,
  // background: theme.palette.primary.contrastText,
  background: 'rgba(17, 8, 0, 0.85)',
  borderRadius: '20px',
  boxShadow: 'none',
  color: 'white',
  height: '100%',
  padding: '24px',

  width: content === 'text' ? '350px' : '100%',
  [theme.breakpoints.down('lg')]: {
    borderRadius: '8px',
    display: 'flex',
    flexDirection: 'column',
  },

  [theme.breakpoints.down('sm')]: {
    width: orientation === 'portrait' ? '350px' : '',
  },
}));

export default function HotspotDialog() {
  const dispatch = useDispatch();
  const theme: any = useTheme();

  // Selector
  const modelId = useSelector(getModelId);
  const userId = useSelector(getUserId);
  const carHotSpotPositions = useSelector(getCarHotSpotPositions);
  const currentCarHotSpotIndex = useSelector(getCurrentCarHotSpotIndex);
  const yaw = useSelector(getYaw);
  const pitch = useSelector(getPitch);
  const carHotSpotCardOpen = useSelector(getCarHotSpotCardOpen);
  const carStatus = useSelector(getCarStatus);
  const isLandscape = useMediaQuery('(orientation: landscape)');

  // States
  const [load, setLoad] = React.useState(true);
  const [mediaSrc, setMediaSrc] = React.useState('');
  const [hotspotData, setHotspotData]: any = React.useState();

  const [featureType, setFeatureType] = React.useState('');

  // Refs
  const ref = React.useRef<any>(null);

  const { handleTop, handleLeft, left } = useHotspotPositions(ref);

  const isHotspotSingle = carHotSpotPositions.length === 1;

  // Media Queries
  const isPortrait = useMediaQuery('(orientation: portrait)');

  const handleClickIncrease = React.useCallback(() => {
    dispatch(currentCarHotSpotIndexSet('increase'));
  }, []);

  const handleClickDecrease = React.useCallback(() => {
    dispatch(currentCarHotSpotIndexSet('decrease'));
  }, []);

  const currentHotspotIndex = React.useMemo(() => {
    let index = carHotSpotPositions?.findIndex(
      (car: any) => car?.id === currentCarHotSpotIndex,
    );
    return ++index;
  }, [currentCarHotSpotIndex]);

  const downloadHotspotMedia = (filename: string) => {
    // send request only content type is image or video
    if (
      filename &&
      hotspotData?.contentType !== 'text' &&
      userId !== undefined &&
      modelId !== undefined
    ) {
      handleDownloadFromGcs(userId, modelId, filename)
        .then((url: any) => setMediaSrc(url))
        .catch(() => {
          setMediaSrc('/placeholder-image.jpg');
        });
    }
  };

  const updateSelectedHotspotData = () => {
    if (!carHotSpotCardOpen) return;

    const hotspotType = carHotSpotPositions[0]?.hasOwnProperty('features')
      ? 'auto'
      : 'normal';

    if (hotspotType === 'auto') {
      setHotspotData(carHotSpotPositions[0]);
      setFeatureType(carHotSpotPositions[0]?.featuresList[0]);
      return;
    }

    const hotspotItem = carHotSpotPositions?.find((report: Report) => {
      // ignore hotspot on car door
      if (report?.description !== process.env.REACT_APP_HOTSPOT_PAYLOAD) {
        return report?.id === currentCarHotSpotIndex;
      }
    });

    setHotspotData(hotspotItem);

    handleTop({
      deltaY: hotspotItem?.y,
      isHotspotSingle,
    });

    handleLeft({ deltaX: hotspotItem?.x });

    if (hotspotItem) {
      downloadHotspotMedia(hotspotItem?.filename);
    }
  };

  React.useEffect(() => {
    if (currentCarHotSpotIndex) updateSelectedHotspotData();
  }, [currentCarHotSpotIndex]);

  const handleClose = () => {
    dispatch(closeHotSpotPositionSet({ undefined }));
    dispatch(carHotSpotCardOpenToggled());
    dispatch(carHotSpotEnableSet(true));
    dispatch(yawSet(yaw + 0.1));
    dispatch(pitchSet(pitch + 0.01));
    setLoad(false);
  };

  const skeletonText = (
    <Box sx={{ pt: 0.5 }}>
      <Skeleton sx={{ background: '#bbb' }} />
      <Skeleton sx={{ background: '#bbb' }} width="60%" />
    </Box>
  );

  const isSingleHotspot = carHotSpotPositions?.length === 1;

  const isHotspotTextEmpty =
    hotspotData?.title === '' && hotspotData?.description === '';

  const { width } = useWindowSize();

  const isSmallDown = useMediaQuery((theme: any) =>
    theme.breakpoints.down('sm'),
  );

  const isMobile = useMediaQuery((theme: any) => theme.breakpoints.down('lg'));

  const minHeightBox: any = React.useMemo(() => {
    return hotspotData?.contentType === 'text'
      ? 'auto'
      : isSmallDown
        ? width * 0.55
        : !isHotspotSingle && isLandscape && isMobile
          ? width * 0.2
          : isLandscape && isMobile
            ? width * 0.25
            : width * 0.3;
  }, [width, isSmallDown, hotspotData?.contentType]);

  const minWidthBox: any = React.useMemo(() => {
    return hotspotData?.contentType === 'text'
      ? 'auto'
      : isLandscape && isMobile
        ? width * 0.4
        : isSmallDown
          ? '100%'
          : width * 0.52;
  }, [width, isSmallDown, hotspotData?.contentType]);

  const RenderHotspotImage = () => {
    if (hotspotData?.contentType === 'image') {
      return (
        <Box
          sx={{
            height: '100%',
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <ImageLoading
            height={'100%'}
            heightLoad={minHeightBox}
            loading={load}
            setLoading={setLoad}
            skeletonBackground="#bbb"
            src={mediaSrc}
            width={'100%'}
            widthLoad={'100%'}
          />
        </Box>
      );
    }
  };

  const RenderHotspotVideo = () => {
    if (hotspotData?.contentType !== 'video') return null;

    if (mediaSrc === '' || !mediaSrc) {
      return (
        <Box
          alignItems={'center'}
          display={'flex'}
          height={'100%'}
          justifyContent={'center'}
          width={'100%'}
        >
          <Skeleton
            height={minHeightBox}
            sx={{ borderRadius: '10px', backgroundColor: '#bbb' }}
            variant="rectangular"
            width={'100%'}
          />
        </Box>
      );
    }

    if (hotspotData?.contentType === 'video') {
      return (
        <CardMedia
          autoPlay
          component="video"
          controls
          onLoad={() => setLoad(true)}
          src={mediaSrc}
          sx={{
            borderRadius: '10px',
            height: '100%',
            width: '100%',
          }}
        />
      );
    }
  };

  const hotspotActions = isSingleHotspot ? null : (
    <Box
      alignItems={'center'}
      display={'flex'}
      justifyContent={'center'}
      padding={'.5rem .5rem 0 .5rem'}
    >
      <Fab
        background={theme.palette.primary.contrastText}
        icon={<ArrowLeftIcon fontSize="small" sx={{ color: 'white' }} />}
        onClick={handleClickDecrease}
        style={{ height: 32, width: 32 }}
      />
      <Box display={'flex'} padding={'.45rem'}>
        {currentHotspotIndex}/{carHotSpotPositions?.length}
      </Box>
      <Fab
        background={theme.palette.primary.contrastText}
        icon={<ArrowRightIcon fontSize="small" sx={{ color: 'white' }} />}
        onClick={handleClickIncrease}
        style={{ height: 32, width: 32 }}
      />
    </Box>
  );

  const renderHotspotText = (
    <Box
      sx={{
        width: '100%',
        height: '100%',
      }}
    >
      {hotspotData?.title && (
        <Typography
          sx={{
            paddingTop: '0.5rem',
            [theme.breakpoints.down('lg')]: {
              marginTop: '0.5rem',
              fontSize: '1.2rem',
              marginBottom: '0.5rem',
            },
          }}
          textAlign={'center'}
          variant="h5"
        >
          {hotspotData?.title}
        </Typography>
      )}

      {hotspotData?.description && (
        <Box
          sx={{
            maxHeight: '7rem',
            paddingTop: '0.5rem',
            [theme.breakpoints.down('lg')]: {
              overflow: 'auto',
              minHeight: '95%',
            },
            '&::-webkit-scrollbar': {
              width: '6px',
            },
          }}
        >
          <Typography
            display="block"
            gutterBottom
            sx={{
              wordBreak: 'break-word',
              margin: 0,
              [theme.breakpoints.down('lg')]: {
                fontSize: '0.75rem',
              },
            }}
            variant="h4"
          >
            {hotspotData?.description}
          </Typography>
        </Box>
      )}
    </Box>
  );

  const renderHotspotTextContent = hotspotData?.hasOwnProperty('features')
    ? null
    : isHotspotTextEmpty
      ? null
      : hotspotData?.contentType === 'text'
        ? renderHotspotText
        : hotspotData?.contentType === 'image' && !load
          ? renderHotspotText
          : hotspotData?.contentType === 'video' && mediaSrc !== ''
            ? renderHotspotText
            : skeletonText;

  const renderCloseButton = (
    <Box
      onClick={handleClose}
      sx={{
        zIndex: (theme) => theme.zIndex.tooltip,
        position: 'initial',
        width: '100%',
        textAlign: 'right',

        '&>button': {
          width: 30,
          height: 20,
          minHeight: 30,
          marginBottom: 1,
          backgroundColor: '#008D47',
          color: '#010101 !important',
        },
      }}
    >
      <FabControlled
        background="#008D47"
        color="#010101"
        Icon={CloseRoundedIcon}
        iconSizeDesktop={'1.2rem'}
      />
    </Box>
  );

  const renderHotspotBox = (
    <Box
      onClick={(e: any) => e.stopPropagation()}
      ref={ref}
      sx={{
        margin: '0 auto',
        justifyContent: 'center',
        alignItems: 'center',
        display: 'flex',
        height: 'fit-content',
      }}
    >
      <Box
        sx={{
          minWidth: minWidthBox,
          width:
            hotspotData?.contentType === 'text'
              ? 'auto'
              : carStatus === 'VDP'
                ? '40%'
                : isLandscape && !isHotspotSingle && isMobile
                  ? '35%'
                  : isLandscape && isHotspotSingle && isMobile
                    ? '45%'
                    : '70%',
          alignItems: 'center',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          position: 'absolute',
          left: hotspotData?.contentType === 'text' ? left : 'unset',
        }}
      >
        <HotspotBox
          content={hotspotData?.contentType}
          orientation={isPortrait ? 'portrait' : 'landscape'}
          sx={{
            minHeight: minHeightBox,
            padding: '32px',
          }}
        >
          <RenderHotspotImage />
          <RenderHotspotVideo />
          {renderHotspotTextContent}

          {hotspotData?.hasOwnProperty('features') && (
            <>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                }}
              >
                <Typography
                  sx={{
                    mb: '16px',
                    fontWeight: '700',
                    fontSize: '24px',
                    minWidth: 'fit-content',
                    [theme.breakpoints.down('lg')]: {
                      fontSize: '20px',
                    },
                  }}
                >
                  Feature List
                </Typography>
                {renderCloseButton}
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  flexWrap: isMobile ? 'wrap' : 'nowrap',
                  justifyCenter: 'center',
                  alignItems: 'center',
                  gap: isMobile ? '8px' : '0',
                  overflow: 'auto',
                  overflowY: 'hidden',
                  padding: isMobile ? '8px' : '0',
                  paddingLeft: isMobile ? '0' : '32px',
                }}
              >
                {hotspotData?.featuresList?.map((item: any) => (
                  <Box
                    key={item}
                    sx={{
                      backgroundColor: isMobile
                        ? 'transparent'
                        : featureType === item
                          ? 'rgba(0, 0, 0, 0.40)'
                          : '',
                      padding: isMobile ? '0' : '10px',
                      borderRadius: '10px 10px 0px 0px',
                      position: 'relative',
                      minWidth: isMobile ? '45%' : 'fit-content',
                    }}
                  >
                    <Box
                      onClick={() => setFeatureType(item)}
                      sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        gap: '12px',
                        alignItems: 'center',
                        cursor: 'pointer',
                        color: featureType === item ? '#010101' : 'white',
                        padding: '10px 16px',
                        borderRadius: '10px',
                        minWidth: 'max-content',
                        border: isMobile
                          ? featureType !== item
                            ? '1px solid #008D47'
                            : 'none'
                          : 'none',
                        fontWeight: featureType === item ? '700' : '400',
                        backgroundColor:
                          featureType === item ? '#008D47' : 'none',
                        fontSize: '16px',
                      }}
                    >
                      {!isMobile && item === 'safety' && <SafetyIcon />}
                      {!isMobile && item === 'comfort' && <ComfortIcon />}
                      {!isMobile && item === 'tech_computer' && (
                        <TechComputerIcon />
                      )}

                      {item === 'tech_computer'
                        ? 'Tech'
                        : item
                            ?.split('_')
                            ?.map(
                              (item: any) =>
                                item.slice(0, 1).toUpperCase() + item.slice(1),
                            )
                            ?.join(' ')}
                    </Box>
                    {!isMobile && featureType === item && (
                      <Box
                        sx={{
                          position: 'absolute',
                          left: '-24px',
                          bottom: '-16px',
                        }}
                      >
                        <LeftCurveIcon />
                      </Box>
                    )}
                    {!isMobile && featureType === item && (
                      <Box
                        sx={{
                          position: 'absolute',
                          right: '-24px',
                          bottom: '-13px',
                        }}
                      >
                        <RightCurveIcon />
                      </Box>
                    )}
                  </Box>
                ))}
              </Box>

              <Grid
                container
                sx={{
                  overflow: 'auto',
                  backgroundColor: 'rgba(0, 0, 0, 0.40)',
                  borderRadius: '16px',
                  padding: '16px 16px 24px 16px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '10px',
                }}
              >
                {hotspotData?.features
                  ?.filter((item: any) => item.type === featureType)
                  .map((feature: any) => (
                    <Accordion
                      defaultExpanded={false}
                      key={feature.name}
                      sx={{
                        '&:before': {
                          display: 'none',
                        },
                        backgroundColor: 'transparent',
                        color: 'white',
                        boxShadow: 'none',
                        backgroundImage: 'none',
                        border: '1px solid #262626',
                        borderRadius: '12px !important',
                        margin: '0 !important',
                        '&:> .Mui-expanded': {
                          margin: '0 !important',
                        },
                      }}
                    >
                      <AccordionSummary
                        aria-controls=""
                        expandIcon={
                          <ExpandMoreIcon
                            sx={{
                              fontSize: '32px',
                              color: 'white',
                            }}
                          />
                        }
                        id="panel1-header"
                        sx={{
                          margin: '0 !important',
                          '&: > .Mui-expanded': {
                            margin: '0 !important',
                          },
                        }}
                      >
                        <Typography
                          sx={{
                            [theme.breakpoints.down('lg')]: {
                              textAlign: 'center',
                              fontSize: '16px',
                            },
                            fontSize: '22px',
                            fontWeight: '700',
                          }}
                        >
                          {feature.name.slice(0, 1).toUpperCase() +
                            feature.name.slice(1)}
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Typography
                          sx={{
                            fontSize: '14px',
                            borderLeft: '4px solid #008D47',
                            paddingLeft: '12px',
                          }}
                        >
                          {feature.description}
                        </Typography>
                      </AccordionDetails>
                    </Accordion>
                  ))}
              </Grid>
            </>
          )}
        </HotspotBox>
        {hotspotActions}
      </Box>
    </Box>
  );

  return (
    <CustomizeBackdrop onClose={handleClose} open={carHotSpotCardOpen}>
      {renderHotspotBox}
    </CustomizeBackdrop>
  );
}
